import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('library')

import { defineStore } from 'pinia'
import type { LibraryState, Title, Bookmark, BookmarkData, PlayedContent } from '@/types'
import { chaptersToTracks } from '@/utils/helpers'
import { ServiceContainer } from '@/services/service-container'
import { StorageService } from '@/services/storage.service'
import { ApiService } from '@/services/api.service'
import { SyncService } from '@/services/sync-v2/reliable-sync-service'
import { useAuthStore } from '@/stores/auth'
import { getConfig } from '@/config'
import { SyncEventType } from '@/types/sync-v2'
import { applyStickyPlayedTimestamps, normalizePlayedAudioProgressFields } from '@/utils/played-content-order'
const CACHE_DURATION = 1000 * 60 * 30 // 30 minutes

// Helper to detect EPUB content format
const detectContentFormat = (title: Title): 'epub' | 'audio' => {
  // Check explicit contentFormat property
  if ((title as any).contentFormat === 'epub') {
    return 'epub'
  }
  
  // Check if this is the Reading Plus app (all titles are EPUBs)
  const config = getConfig()
  if (config.app.id === 'reading-plus') {
    return 'epub'
  }
  
  // Check for EPUB-specific properties
  if (title.chapters && title.chapters.some((ch: any) => ch.epubUrl)) {
    return 'epub'
  }
  
  // Default to audio
  return 'audio'
}

export const useLibraryStore = defineStore('library', {
  state: (): LibraryState & { lastSync: number | null } => ({
    purchased: [],
    bookmarked: [], // Empty - managed by bookmark services
    finished: [],
    played_content: [], // Empty - managed by bookmark services
    listenedTitles: [],
    liked: [],
    loading: false,
    error: null,
    lastSync: null
  }),

  actions: {
    // Helper method to remove duplicates from arrays
    removeDuplicatesById(array: Title[] | null | undefined): Title[] {
      if (!array) return [];
      
      // Safety check: ensure array is actually an array
      if (!Array.isArray(array)) {
        fileLogger.error('removeDuplicatesById received non-array value:', typeof array, array);
        return [];
      }
      
      const uniqueIds = new Set<string>();
      return array.filter(item => {
        // Get ID from item.id or title_id, ensure it's a valid string
        const itemId = item.id || (item as any).title_id
        if (!itemId) {
          fileLogger.warn('Item missing id and title_id, filtering out:', item);
          return false;
        }
        const idString = String(itemId);
        // Skip items with invalid ID strings
        if (idString === 'undefined' || idString === 'null' || idString === '') {
          fileLogger.warn('Item has invalid id string, filtering out:', { idString, item });
          return false;
        }
        if (uniqueIds.has(idString)) {
          fileLogger.log(`Removing duplicate title with ID: ${idString}`);
          return false;
        }
        uniqueIds.add(idString);
        return true;
      });
    },

    async loadLibraryData(force = false) {
      console.group('Library Store - loadLibraryData')
      fileLogger.log('Starting load with force:', force)
      
      // Check authentication status first
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        fileLogger.log('User is not authenticated, skipping library load')
        this.loading = false
        console.groupEnd()
        return
      }
      
      try {
        this.loading = true
        this.error = null
        
        const services = ServiceContainer.getInstance()
        fileLogger.log('Got service container')
        
        const storage = await services.get<StorageService>('storage')
        const syncService = await services.get<SyncService>('sync')
        fileLogger.log('Services initialized:', { storage: !!storage, syncService: !!syncService })

        // Load from localStorage first (use same keys as sync/writes: finished_titles not 'finished')
        fileLogger.log('Loading from localStorage...')
        const cachedData = await Promise.all([
          storage.get<Title[]>('liked'),
          storage.get<Title[]>('bookmarked'),
          storage.get<Title[]>('finished_titles'),
          storage.get<Title[]>('played_content'),
          storage.get<Title[]>('purchased') || []
        ])
        
        // First normalize IDs, then filter
        const normalizedCachedPlayedContent = (cachedData[3] || []).map(item => {
          const itemId = item.id || (item as any).title_id
          if (!item.id && itemId) {
            return { ...item, id: String(itemId) }
          }
          if (item.id) {
            return { ...item, id: String(item.id) }
          }
          return item
        })
        
        // Filter out EPUB position records and removed items, then dedupe by id (avoids cache/sync duplicates)
        const filteredPlayedContent = this.removeDuplicatesById(
          normalizedCachedPlayedContent.filter(item => {
            const id = String(item.id || '')
            const isRemoved = (item as any).removed === true
            return id && id !== 'undefined' && id !== 'null' && !id.startsWith('epub-') && !id.startsWith('audio-') && !isRemoved
          })
        ).map(item => normalizePlayedAudioProgressFields(item as any))

        fileLogger.log('Cached data loaded:', {
          liked: cachedData[0]?.length || 0,
          bookmarked: cachedData[1]?.length || 0,
          finished: cachedData[2]?.length || 0,
          played_content: filteredPlayedContent?.length || 0,
          purchased: cachedData[4]?.length || 0
        })
        
        // Debug purchased data specifically
        if (cachedData[4] && cachedData[4].length > 0) {
          fileLogger.log('Local purchased data found:', cachedData[4].map(t => ({ id: t.id, title: t.title })))
        }

        // Save current store state to compare with after sync
        const prevState = {
          liked: this.liked.length,
          bookmarked: this.bookmarked.length,
          finished: this.finished.length,
          played_content: this.played_content.length,
          purchased: this.purchased.length
        };

        // First update store with local data
        if (cachedData.some(data => data !== null)) {
          fileLogger.log('Updating store with cached data')
          this.updateStoreFromData({
            liked: cachedData[0],
            bookmarked: cachedData[1],
            finished: cachedData[2],
            played_content: filteredPlayedContent,
            purchased: cachedData[4]
          })
          
          // Sync system handles all data loading - no separate API calls needed
        }

        // Check if app is online
        const isOnline = navigator.onLine
        
        // Sync logic:
        // 1. Always sync if forced
        // 2. Always sync if online and no previous sync
        // 3. Always sync if online and it's been more than 5 minutes since last sync
        // 4. If offline, only sync if forced (to avoid unnecessary API calls)
        const shouldSync = force || (isOnline && (!this.lastSync || (Date.now() - this.lastSync > 5 * 60 * 1000)))
        
        if (shouldSync) {
          if (!isOnline) {
            fileLogger.log('App is offline, skipping sync (using cached data)')
          } else {
            // Sync with server - Sync V2 will handle merging
            fileLogger.log('App is online, starting server sync...')
            const userId = authStore.user?.id ? String(authStore.user.id) : null
            if (!userId) {
              fileLogger.warn('Cannot sync: No user ID')
              return
            }
            // Use forceFull when force=true to get all data from server
            const syncResult = await syncService.fullSync(userId, force)
            fileLogger.log('Sync result:', syncResult)
          
            if (syncResult.success) {
              // Update the last sync timestamp
              this.lastSync = Date.now()
              
              // CRITICAL: Always reload from storage after sync completes
              // This ensures the store reflects the latest synced data
              // Wait a brief moment to ensure storage writes are complete
              await new Promise(resolve => setTimeout(resolve, 100))
              
              // Load the merged data from localStorage (syncUserData has already merged and stored it)
              fileLogger.log('Loading merged data after sync...')
              const freshData = await Promise.all([
                storage.get<Title[]>('liked'),
                storage.get<Title[]>('bookmarked'),
                storage.get<Title[]>('finished_titles'),
                storage.get<Title[]>('played_content'),
                storage.get<Title[]>('purchased')
              ])
              
              // First normalize IDs, then filter
              const normalizedFreshPlayedContent = (freshData[3] || []).map(item => {
                const itemId = item.id || (item as any).title_id
                if (!item.id && itemId) {
                  return { ...item, id: String(itemId) }
                }
                if (item.id) {
                  return { ...item, id: String(item.id) }
                }
                return item
              })
              
              // Filter out EPUB position records and removed items, then dedupe (avoids merge duplicates)
              const filteredFreshPlayedContent = this.removeDuplicatesById(
                normalizedFreshPlayedContent.filter(item => {
                  const id = String(item.id || '')
                  const isRemoved = (item as any).removed === true
                  return id && id !== 'undefined' && id !== 'null' && !id.startsWith('epub-') && !id.startsWith('audio-') && !isRemoved
                })
              ).map(item => normalizePlayedAudioProgressFields(item as any))

              fileLogger.log('Merged data loaded:', {
                liked: freshData[0]?.length || 0,
                bookmarked: freshData[1]?.length || 0,
                finished: freshData[2]?.length || 0,
                played_content: filteredFreshPlayedContent?.length || 0,
                purchased: freshData[4]?.length || 0
              })
              
              // Debug purchased data after sync
              if (freshData[4] && freshData[4].length > 0) {
                fileLogger.log('Purchased data after sync:', freshData[4].map(t => ({ id: t.id, title: t.title })))
              } else {
                fileLogger.log('No purchased data after sync - this is correct')
              }

              // Preserve finished titles that were added locally during sync (avoid race where
              // sync read stale local data and would overwrite with merge missing the new title)
              const syncedFinished = freshData[2] || []
              const syncedFinishedIds = new Set(syncedFinished.map(t => String(t.id)))
              const localOnlyFinished = this.finished.filter(t => !syncedFinishedIds.has(String(t.id)))
              const mergedFinished =
                localOnlyFinished.length > 0
                  ? [...syncedFinished, ...localOnlyFinished]
                  : syncedFinished
              if (localOnlyFinished.length > 0) {
                fileLogger.log(
                  `Preserving ${localOnlyFinished.length} locally-added finished title(s) after sync`
                )
              }

              // Update store with the merged data
              fileLogger.log('About to update store with data:', {
                liked: freshData[0]?.length || 0,
                bookmarked: freshData[1]?.length || 0,
                finished: mergedFinished.length,
                played_content: filteredFreshPlayedContent?.length || 0,
                purchased: freshData[4]?.length || 0
              })
              
              // Debug purchased data before updating store
              if (freshData[4] && freshData[4].length > 0) {
                fileLogger.log('WARNING: Purchased data found before store update:', freshData[4].map(t => ({ id: t.id, title: t.title })))
              } else {
                fileLogger.log('Purchased data is empty before store update - this is correct')
              }
              
              // Debug current store state before update
              fileLogger.log('Current store state before update:', {
                purchased: this.purchased.length,
                bookmarked: this.bookmarked.length,
                finished: this.finished.length,
                played_content: this.played_content.length,
                liked: this.liked.length
              })
              
              this.updateStoreFromData({
                liked: freshData[0],
                bookmarked: freshData[1],
                finished: mergedFinished,
                played_content: filteredFreshPlayedContent,
                purchased: freshData[4]
              })
              
              // CRITICAL: Re-deduplicate after updating from sync
              // This prevents duplicates that might have been introduced during sync
              this.purchased = this.removeDuplicatesById(this.purchased)
              this.bookmarked = this.removeDuplicatesById(this.bookmarked)
              this.finished = this.removeDuplicatesById(this.finished)
              this.played_content = this.removeDuplicatesById(this.played_content)
              this.liked = this.removeDuplicatesById(this.liked)
              
              // Save deduplicated data back to storage
              await Promise.all([
                storage.set('purchased', this.purchased),
                storage.set('bookmarked', this.bookmarked),
                storage.set('finished_titles', this.finished),
                storage.set('played_content', this.played_content),
                storage.set('liked', this.liked)
              ])
              
              // Debug store state after update
              fileLogger.log('Store state after update and deduplication:', {
                purchased: this.purchased.length,
                bookmarked: this.bookmarked.length,
                finished: this.finished.length,
                played_content: this.played_content.length,
                liked: this.liked.length
              })
              
              // Sync system handles all data loading - no separate API calls needed
            }
          }
        } else {
          if (!isOnline) {
            fileLogger.log('Skipping sync, app is offline (using cached data)')
          } else {
            fileLogger.log('Skipping sync, using cached data (last sync was recent)')
          }
        }

        // After loading data, ensure we have no duplicates in each section
        // This is critical to prevent multiple copies of the same book
        this.purchased = this.removeDuplicatesById(this.purchased);
        this.bookmarked = this.removeDuplicatesById(this.bookmarked);
        this.finished = this.removeDuplicatesById(this.finished);
        this.played_content = this.removeDuplicatesById(this.played_content);
        this.liked = this.removeDuplicatesById(this.liked);
        
        // Filter out bookmarked titles that have no non-removed bookmarks
        this.bookmarked = this.bookmarked.filter(title => {
          if (!title.bookmarks || !Array.isArray(title.bookmarks)) {
            return false
          }
          const nonRemovedBookmarks = title.bookmarks.filter((bm: any) => bm.removed !== true)
          return nonRemovedBookmarks.length > 0
        })
        
        // Save deduplicated arrays back to storage
        // This ensures storage is always clean, even if sync adds duplicates
        await Promise.all([
          storage.set('purchased', this.purchased),
          storage.set('bookmarked', this.bookmarked),
          storage.set('finished_titles', this.finished),
          storage.set('played_content', this.played_content),
          storage.set('liked', this.liked)
        ]);
        
        fileLogger.log('Deduplication complete and saved to storage:', {
          purchased: this.purchased.length,
          bookmarked: this.bookmarked.length,
          finished: this.finished.length,
          played_content: this.played_content.length,
          liked: this.liked.length
        })

      } catch (error) {
        fileLogger.error('Failed to load library data:', error)
        this.error = error instanceof Error ? error.message : 'Failed to load library data'
      } finally {
        this.loading = false
        fileLogger.log('Final store state:', {
          liked: this.liked.length,
          bookmarked: this.bookmarked.length,
          finished: this.finished.length,
          played: this.played_content.length,
          purchased: this.purchased.length
        })
        console.groupEnd()
      }
    },

    // Removed loadTitleDataForBookmarksAndPlayed - sync system handles everything

    updateStoreFromData(data: {
      liked?: Title[] | null,
      bookmarked?: Title[] | null,
      finished?: Title[] | null,
      played_content?: Title[] | null,
      purchased?: Title[] | null
    }) {
      // Always update purchased, even if empty array
      if (data.purchased !== undefined) {
        const purchasedData = Array.isArray(data.purchased) ? data.purchased : []
        this.purchased = this.removeDuplicatesById(purchasedData).map(title => ({
          ...title,
          tracks: chaptersToTracks(title, title.chapters || [])
        }))
        fileLogger.log('Updated purchased store:', this.purchased.length, 'items')
      }
      
      // Always update bookmarked, even if empty array
      if (data.bookmarked !== undefined) {
        const bookmarkedData = Array.isArray(data.bookmarked) ? data.bookmarked : []
        this.bookmarked = this.removeDuplicatesById(bookmarkedData)
          .map(title => ({
            ...title,
            tracks: chaptersToTracks(title, title.chapters || [])
          }))
          .filter(title => {
            // Filter out titles that have no non-removed bookmarks
            if (!title.bookmarks || !Array.isArray(title.bookmarks)) {
              return false
            }
            const nonRemovedBookmarks = title.bookmarks.filter((bm: any) => bm.removed !== true)
            return nonRemovedBookmarks.length > 0
          })
        fileLogger.log('Updated bookmarked store:', this.bookmarked.length, 'items (filtered out titles with no non-removed bookmarks)')
      }
      
      // Always update finished, even if empty array
      if (data.finished !== undefined) {
        const finishedData = Array.isArray(data.finished) ? data.finished : []
        // Filter out items with removed: true flag
        const validFinished = finishedData.filter((item: any) => item.removed !== true)
        // Normalize id from title_id so menu/library lookups always match
        const normalizedFinished = validFinished.map((item: any) => {
          const itemId = item.id ?? item.title_id
          return itemId !== undefined && itemId !== null && itemId !== ''
            ? { ...item, id: String(itemId) }
            : item
        })
        this.finished = this.removeDuplicatesById(normalizedFinished).map(title => ({
          ...title,
          tracks: chaptersToTracks(title, title.chapters || [])
        }))
        fileLogger.log('Updated finished store:', this.finished.length, 'items (filtered out removed items)')
      }
      
      // Always update played_content, even if empty array
      if (data.played_content !== undefined) {
        const playedContentData = Array.isArray(data.played_content) ? data.played_content : []
        fileLogger.log('Processing played_content data:', playedContentData.length, 'items')
        
        // First, normalize all items to have an id field
        const normalizedItems = playedContentData.map(item => {
          // Ensure all items have an id field (use title_id if id is missing)
          const itemId = item.id || (item as any).title_id
          if (!item.id && itemId) {
            return {
              ...item,
              id: String(itemId)
            }
          }
          // Ensure id is a string
          if (item.id) {
            return {
              ...item,
              id: String(item.id)
            }
          }
          return item
        })
        
        // Filter out any EPUB position records and removed items
        // Accept items that have a valid id (not starting with epub-/audio-) and are not removed
        const titleObjects = normalizedItems.filter(item => {
          const id = String(item.id || '')
          const isRemoved = (item as any).removed === true
          // Filter out EPUB/audio position records, removed items, and items without valid IDs
          const isValid = id && id !== 'undefined' && id !== 'null' && !id.startsWith('epub-') && !id.startsWith('audio-') && !isRemoved
          if (!isValid && id) {
            fileLogger.log('Filtered out played_content item:', { id, isRemoved, startsWithEpub: id.startsWith('epub-'), startsWithAudio: id.startsWith('audio-') })
          }
          return isValid
        })
        
        fileLogger.log('After filtering:', titleObjects.length, 'items')
        
        this.played_content = this.removeDuplicatesById(titleObjects).map(title => ({
          ...normalizePlayedAudioProgressFields(title as any),
          tracks: chaptersToTracks(title, title.chapters || [])
        }))
        fileLogger.log('Updated played_content store:', this.played_content.length, 'items (after deduplication)')
      }
      
      // Always update liked, even if empty array
      if (data.liked !== undefined) {
        // Safety check: ensure liked is an array
        const likedData = Array.isArray(data.liked) ? data.liked : (data.liked ? [data.liked] : [])
        // Filter out items with is_liked: false (these are unliked items)
        const validLikedData = likedData.filter((item: any) => {
          const isUnliked = item.is_liked === false
          if (isUnliked) {
            fileLogger.log('Filtering out unliked item from liked store:', item.id)
          }
          return !isUnliked
        })
        this.liked = this.removeDuplicatesById(validLikedData).map(title => ({
          ...title,
          tracks: chaptersToTracks(title, title.chapters || [])
        }))
        fileLogger.log('Updated liked store:', this.liked.length, 'items (filtered out unliked items)')
      }
    },

    async addToFinished(title: Title) {
      // Remove any existing instances first - ensure string comparison
      this.finished = this.finished.filter(t => String(t.id) !== String(title.id))
      
      // Add content format detection and then add the new title
      const enhancedTitle = {
        ...title,
        contentFormat: detectContentFormat(title),
        tracks: chaptersToTracks(title, title.chapters || [])
      }
      this.finished.push(enhancedTitle)
      fileLogger.log('Added title to finished collection:', title.id)
      
      // Save to storage
      const services = ServiceContainer.getInstance()
      const storage = await services.get<StorageService>('storage')
      await storage.set('finished_titles', this.finished)
    },

    async markTitleAsFinished(title: Title) {
      try {
        // Skip if already finished - ensure string comparison
        if (this.finished.some(t => String(t.id) === String(title.id))) {
          fileLogger.log('Title already marked as finished:', title.id)
          return true
        }
        
        // Remove any existing instances first - ensure string comparison
        this.finished = this.finished.filter(t => String(t.id) !== String(title.id))
        
        // Add to finished titles
        this.finished.push(title)
        fileLogger.log('Title marked as finished:', title.id)
        
        // Save to local storage immediately
        const services = ServiceContainer.getInstance()
        const storage = await services.get<StorageService>('storage')
        const syncService = await services.get<SyncService>('sync')
        
        await storage.set('finished_titles', this.finished)
        
        // Queue finish action for sync
        await syncService.queueAction(
          SyncEventType.finish,
          String(title.id),
          {
            title_id: title.id
          }
        )
        
        fileLogger.log('Title marked as finished successfully')
        return true
      } catch (error) {
        fileLogger.error('Error marking title as finished:', error)
        return false
      }
    },

    async addToPurchased(title: Title) {
      // Check if title is already in purchased collection - ensure string comparison
      if (this.purchased.some(t => String(t.id) === String(title.id))) {
        fileLogger.log('Title already in purchased collection:', title.id)
        return
      }
      
      // Remove any existing instances first - ensure string comparison
      this.purchased = this.purchased.filter(t => String(t.id) !== String(title.id))
      
      // Add the title to purchased collection
      this.purchased.push(title)
      fileLogger.log('Added title to purchased:', title.id)
      
      // Save to storage immediately
      const services = ServiceContainer.getInstance()
      const storage = await services.get<StorageService>('storage')
      const syncService = await services.get<SyncService>('sync')
      
      await storage.set('purchased', this.purchased)
      
      // Queue purchase action for sync
      await syncService.queueAction(
        SyncEventType.purchase,
        String(title.id),
        {
          title_id: title.id
        }
      ).catch(error => {
        fileLogger.error('Error queueing purchase action:', error)
      })
    },

    async removeFromPurchased(title: Title) {
      // Remove the title from purchased collection using filter - ensure string comparison
      const oldLength = this.purchased.length;
      this.purchased = this.purchased.filter(t => String(t.id) !== String(title.id));
      
      // Log result based on whether the title was found and removed
      if (oldLength !== this.purchased.length) {
        fileLogger.log('Removed title from purchased:', title.id);
      } else {
        fileLogger.log('Title not found in purchased collection:', title.id);
        return;
      }
      
      // Save to storage immediately
      const services = ServiceContainer.getInstance()
      const storage = await services.get<StorageService>('storage')
      await storage.set('purchased', this.purchased)
      
      // Sync with backend
      this.syncLibrary().catch(error => {
        fileLogger.error('Error syncing after removing from purchased:', error)
      })
    },

    async toggleFavorite(title: Title) {
      // Ensure string comparison
      const isLiked = this.liked.some(t => String(t.id) === String(title.id))
      
      const services = ServiceContainer.getInstance()
      const storage = await services.get<StorageService>('storage')
      const syncService = await services.get<SyncService>('sync')
      
      if (isLiked) {
        // Remove from liked - ensure string comparison
        this.liked = this.liked.filter(t => String(t.id) !== String(title.id))
        fileLogger.log('Removed title from liked:', title.id)
        
        // Queue unlike action for sync
        await syncService.queueAction(
          SyncEventType.unlike,
          String(title.id),
          {
            title_id: title.id,
            is_liked: false,
            created_at: new Date().toISOString()
          }
        )
      } else {
        // Remove any existing instances first - ensure string comparison
        this.liked = this.liked.filter(t => String(t.id) !== String(title.id))
        
        // Add to liked with timestamp
        const titleWithTimestamp = {
          ...title,
          likedAt: new Date().toISOString() // Track when item was liked
        }
        this.liked.push(titleWithTimestamp)
        fileLogger.log('Added title to liked:', title.id)
        
        // Queue like action for sync
        await syncService.queueAction(
          SyncEventType.like,
          String(title.id),
          {
            title_id: title.id,
            is_liked: true,
            created_at: new Date().toISOString()
          }
        )
      }
      
      // Save to storage
      await storage.set('liked', this.liked)
      
      return !isLiked
    },

    // Add a new method for bookmarking titles
    async toggleBookmark(title: Title) {
      // Ensure string comparison
      const isBookmarked = this.bookmarked.some(t => String(t.id) === String(title.id))
      
      const services = ServiceContainer.getInstance()
      const storage = await services.get<StorageService>('storage')
      const syncService = await services.get<SyncService>('sync')
      
      if (isBookmarked) {
        // Get existing bookmarks for this title before removing (for sync)
        const existingTitle = this.bookmarked.find(t => String(t.id) === String(title.id))
        const bookmarksToRemove = existingTitle?.bookmarks || []
        
        // Remove from bookmarked - ensure string comparison
        this.bookmarked = this.bookmarked.filter(t => String(t.id) !== String(title.id))
        fileLogger.log('Removed title from bookmarks:', title.id)
        
        // Queue unbookmark action for sync
        // Send all bookmarks with removed: true flag
        await syncService.queueAction(
          SyncEventType.unbookmark,
          String(title.id),
          {
            title_id: title.id,
            bookmarks: bookmarksToRemove.map((bm: Bookmark) => ({
              ...bm,
              removed: true
            }))
          }
        )
      } else {
        // Remove any existing instances first - ensure string comparison
        this.bookmarked = this.bookmarked.filter(t => String(t.id) !== String(title.id))
        
        // Add to bookmarked
        this.bookmarked.push(title)
        fileLogger.log('Added title to bookmarks:', title.id)
        fileLogger.log('Current bookmarked array length:', this.bookmarked.length)
        
        // Add original bookmark data to the title
        const contentFormat = detectContentFormat(title)
        const bookmark: Bookmark = {
          id: `bookmark-${title.id}-${Date.now()}`,
          title: 'Bookmark',
          content_format: contentFormat,
          created_at: new Date().toISOString(),
          position: 0,
          chapter_id: 0,
          note: ''
        }
        
        // Add to title's bookmarks array
        const titleWithBookmark = {
          ...title,
          bookmarks: [bookmark]
        }
        
        // Update the title in the bookmarked array
        this.bookmarked = this.bookmarked.map(t => 
          String(t.id) === String(title.id) ? titleWithBookmark : t
        )
        
        fileLogger.log('Added original bookmark data to title:', title.id)
        fileLogger.log('Updated bookmarked array length:', this.bookmarked.length)
        fileLogger.log('Title with bookmark data:', titleWithBookmark)
        
        // Queue bookmark action for sync
        await syncService.queueAction(
          SyncEventType.bookmark,
          String(title.id),
          {
            title_id: title.id,
            bookmarks: [bookmark]
          }
        )
      }
      
      // Save to storage
      await storage.set('bookmarked', this.bookmarked)
      
      return !isBookmarked
    },

    async syncPlaybackProgress(userId: number, trackId: number, progress: number) {
      const services = ServiceContainer.getInstance()
      const syncService = await services.get<SyncService>('sync')
      return syncService.syncPlaybackProgress(userId, trackId, progress)
    },

    async syncWithBackend() {
      try {
        const services = ServiceContainer.getInstance()
        const syncService = await services.get('sync')
        const storage = await services.get<StorageService>('storage')
        const authStore = useAuthStore()
        
        const userId = authStore.user?.id ? String(authStore.user.id) : null
        if (!userId) {
          fileLogger.warn('Cannot sync: No user ID')
          return false
        }
        
        // Use Sync V2 fullSync method to sync all user data
        // Pass forceFull=true to get all data from server, not just changes
        const syncResult = await syncService.fullSync(userId, true)
        
        if (syncResult.success) {
          this.lastSync = Date.now()
          fileLogger.log('Library data synced with backend, reloading from storage...')
          
          // Reload library data from storage after sync to reflect server changes
          const freshData = await Promise.all([
            storage.get<Title[]>('liked'),
            storage.get<Title[]>('bookmarked'),
            storage.get<Title[]>('finished_titles'),
            storage.get<Title[]>('played_content'),
            storage.get<Title[]>('purchased') || []
          ])
          
          // First normalize IDs, then filter
          const normalizedSyncPlayedContent = (freshData[3] || []).map(item => {
            const itemId = item.id || (item as any).title_id
            if (!item.id && itemId) {
              return { ...item, id: String(itemId) }
            }
            if (item.id) {
              return { ...item, id: String(item.id) }
            }
            return item
          })
          
          // Filter out EPUB position records and removed items, then dedupe
          const filteredPlayedContent = this.removeDuplicatesById(
            normalizedSyncPlayedContent.filter(item => {
              const id = String(item.id || '')
              const isRemoved = (item as any).removed === true
              return id && id !== 'undefined' && id !== 'null' && !id.startsWith('epub-') && !id.startsWith('audio-') && !isRemoved
            })
          )

          // Update store with synced data
          this.updateStoreFromData({
            liked: freshData[0],
            bookmarked: freshData[1],
            finished: freshData[2],
            played_content: filteredPlayedContent,
            purchased: freshData[4]
          })
          
          fileLogger.log('Library reloaded from storage after sync:', {
            liked: this.liked.length,
            bookmarked: this.bookmarked.length,
            finished: this.finished.length,
            played_content: this.played_content.length,
            purchased: this.purchased.length
          })
        } else {
          fileLogger.warn('Failed to sync library data with backend:', syncResult.errorMessage)
        }
        
        return syncResult.success
      } catch (error) {
        fileLogger.error('Error syncing with backend:', error)
        return false
      }
    },
    
    // New method to sync library data with the backend
    async syncLibrary() {
      fileLogger.log('syncLibrary called')
      
      try {
        const authStore = useAuthStore()
        if (!authStore.isAuthenticated) {
          fileLogger.log('User not authenticated, skipping sync')
          return false
        }
        
        // First, save current state to local storage
        const services = ServiceContainer.getInstance()
        const storage = await services.get<StorageService>('storage')
        
        fileLogger.log('Saving to local storage')
        await Promise.all([
          storage.set('liked', this.liked),
          storage.set('bookmarked', this.bookmarked),
          storage.set('finished_titles', this.finished),
          storage.set('played_content', this.played_content),
          storage.set('purchased', this.purchased)
        ])
        
        // Then sync with backend
        fileLogger.log('Syncing with backend')
        const syncService = await services.get<SyncService>('sync')
        
        // Extract bookmarks from bookmarked titles AND the bookmarks storage key
        const bookmarksByTitle = new Map<string, BookmarkData>()
        const storedBookmarks = await storage.get<BookmarkData[]>('bookmarks') || []
        for (const bookmarkData of storedBookmarks) {
          if (!bookmarkData?.title_id || !bookmarkData.bookmarks?.length) continue
          bookmarksByTitle.set(String(bookmarkData.title_id), bookmarkData)
        }
        this.bookmarked.forEach(title => {
          if (!title.bookmarks?.length) return
          const titleId = String(title.id)
          const existing = bookmarksByTitle.get(titleId)
          if (existing) {
            const mergedIds = new Set(existing.bookmarks.map((b: any) => b.id))
            for (const bm of title.bookmarks) {
              if (!mergedIds.has(bm.id)) {
                existing.bookmarks.push(bm)
              }
            }
          } else {
            bookmarksByTitle.set(titleId, {
              title_id: title.id,
              bookmarks: title.bookmarks,
            })
          }
        })
        const bookmarks = Array.from(bookmarksByTitle.values())
        
        // Get played content data for syncing
        const playedContent = await storage.get<PlayedContent[]>('played_content') || []
        
        // Use the syncService to push all data to the server
        const syncSuccessful = await syncService.pushSyncData({
          liked: this.liked,
          bookmarked: bookmarks,
          finished: this.finished,
          played_content: playedContent,
          purchased: this.purchased
        })
        
        if (syncSuccessful) {
          fileLogger.log('Library data synced with backend')
          this.lastSync = Date.now()
        } else {
          fileLogger.warn('Failed to sync library data with backend')
        }
        
        return syncSuccessful
      } catch (error) {
        fileLogger.error('Error syncing library with backend:', error)
        return false
      }
    },

    // Reset all library store state to initial values
    reset() {
      fileLogger.log('Resetting store state')
      this.purchased = []
      this.bookmarked = []
      this.finished = []
      this.played_content = []
      this.listenedTitles = []
      this.liked = []
      this.loading = false
      this.error = null
      this.lastSync = null
    },

    // Add a helper method to check if a title is purchased
    isPurchased(titleId: string | number): boolean {
      // Ensure string comparison
      const idString = String(titleId);
      return this.purchased.some(title => String(title.id) === idString)
    },

    async addToPlayed(title: Title) {
      // Add to played content (display) with sticky dateAdded for Continue Listening order.
      // Mutate the in-memory list synchronously before any await so concurrent
      // updatePlaybackPosition calls see the title and do not create duplicates.
      const existing = this.played_content.find(t => String(t.id) === String(title.id))
      const resolvedPosition =
        title.queuePosition ??
        (title as any).position ??
        existing?.queuePosition ??
        (existing as any)?.position
      const resolvedIndex =
        title.queueIndex ??
        (title as any).chapter_id ??
        existing?.queueIndex ??
        (existing as any)?.chapter_id
      const entry = applyStickyPlayedTimestamps(existing, {
        ...title,
        ...normalizePlayedAudioProgressFields({
          queuePosition: resolvedPosition,
          position: resolvedPosition,
          queueIndex: resolvedIndex,
          chapter_id: (title as any).chapter_id ?? (existing as any)?.chapter_id ?? resolvedIndex,
        }),
      }, { newPosition: resolvedPosition })

      this.played_content = this.played_content.filter(t => String(t.id) !== String(title.id))
      this.played_content.push(entry)
      fileLogger.log('Added title to played content:', title.id)

      const services = ServiceContainer.getInstance()
      const syncService = await services.get<SyncService>('sync')
      if (syncService && typeof syncService.removeFromRemovedPlayedContentCache === 'function') {
        await syncService.removeFromRemovedPlayedContentCache(String(title.id))
      }

      // Save display titles
      const storage = await services.get<StorageService>('storage')
      await storage.set('played_content', this.played_content)

      fileLogger.log('Persisted title to played content:', title.id)
    },

    async removeFromPlayed(title: Title) {
      // Remove from played_content - ensure string comparison
      const oldLength = this.played_content.length
      this.played_content = this.played_content.filter(t => String(t.id) !== String(title.id))
      
      // Log result based on whether the title was found and removed
      if (oldLength !== this.played_content.length) {
        fileLogger.log('Removed title from played_content:', title.id)
        
        // Get the played content data from storage before removing
        const services = ServiceContainer.getInstance()
        const syncService = await services.get<SyncService>('sync')
        const storage = await services.get<StorageService>('storage')
        
        // Get the played content item to get position/index for sync
        const allPlayedContent = await storage.get<PlayedContent[]>('played_content') || []
        const epubPlayedContent = await storage.get<PlayedContent[]>('epub_played_content') || []
        const playedContentItem = [...allPlayedContent, ...epubPlayedContent].find(
          item => String(item.title_id || (item as any).id) === String(title.id)
        )
        
        // Save to storage (after getting the item data)
        await storage.set('played_content', this.played_content)
        
        // Queue a playProgress action with removed: true for just this title
        if (syncService) {
          const titleId = typeof title.id === 'number' ? title.id : parseInt(String(title.id), 10) || 0
          const queueIndex = playedContentItem?.chapter_id || (playedContentItem as any)?.queueIndex || 0
          const queuePosition = playedContentItem?.position || (playedContentItem as any)?.queuePosition || 0
          
          await syncService.queueAction(
            SyncEventType.playProgress,
            String(title.id),
            {
              titleId: titleId,
              queueIndex: queueIndex,
              queuePosition: queuePosition,
              removed: true, // Mark as removed
              timestamp: new Date().toISOString(),
            }
          )
          fileLogger.log(`Queued playProgress removal (removed: true) for played_content item ${title.id}`)
          
          // Also add to removed cache for tracking
          if (playedContentItem) {
            await syncService.addToRemovedPlayedContentCache(String(title.id), playedContentItem)
          } else {
            // Create a minimal PlayedContent object if not found
            const minimalPlayedContent: PlayedContent = {
              id: String(title.id),
              title_id: String(title.id),
              content_format: 'audio',
              created_at: new Date().toISOString(),
              position: 0,
              chapter_id: 0
            }
            await syncService.addToRemovedPlayedContentCache(String(title.id), minimalPlayedContent)
          }
        }
      } else {
        fileLogger.log('Title not found in played_content collection:', title.id)
      }
    },

    async removeFromFinished(title: Title) {
      const titleId = String(title.id || (title as any).title_id || '')
      if (!titleId || titleId === 'undefined' || titleId === 'null') {
        fileLogger.warn('Cannot remove from finished: missing title id', title)
        return
      }

      const matchesTitle = (t: Title) =>
        String(t.id || (t as any).title_id) === titleId

      // Remove from finished - ensure string comparison (id or title_id)
      const oldLength = this.finished.length
      this.finished = this.finished.filter(t => !matchesTitle(t))
      
      // Log result based on whether the title was found and removed
      if (oldLength !== this.finished.length) {
        fileLogger.log('Removed title from finished:', titleId)
        
        const services = ServiceContainer.getInstance()
        const syncService = await services.get<SyncService>('sync')
        const storage = await services.get<StorageService>('storage')
        
        // Save to storage first so local UI stays correct even if sync fails
        await storage.set('finished_titles', this.finished)

        // Sync V2: queue finish with removed:true (addToRemovedFinishedCache is a no-op there).
        // Legacy sync: also keep removed cache for full-sync payload.
        if (syncService) {
          if (typeof syncService.queueAction === 'function') {
            await syncService.queueAction(
              SyncEventType.finish,
              titleId,
              {
                title_id: titleId,
                removed: true,
              }
            )
            fileLogger.log(`Queued finish removal (removed: true) for finished item ${titleId}`)
          }

          if (typeof syncService.addToRemovedFinishedCache === 'function') {
            await syncService.addToRemovedFinishedCache(titleId, title)
          }
        }
        
        fileLogger.log('Removed title from finished and queued API sync')
      } else {
        fileLogger.log('Title not found in finished collection:', titleId)
      }
    }
  }
}) 